import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Alert, Card } from 'react-bootstrap';

const BASForm = ({ formData, onSubmit, onCancel }) => {
  const [form, setForm] = useState({
    form_type: 'BAS',
    period_start: '',
    period_end: '',
    abn: '',
    business_name: '',
    gst_sales: '',
    gst_purchases: '',
    gst_payable: '',
    gst_refund: '',
    net_gst: '',
    payg_withholding: '',
    payg_instalment: '',
    fbt: '',
    wine_equalisation: '',
    luxury_car: '',
    fuel_tax_credit: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (formData) {
      setForm(formData);
    }
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.period_start) newErrors.period_start = 'Period start date is required';
    if (!form.period_end) newErrors.period_end = 'Period end date is required';
    if (form.period_start && form.period_end && new Date(form.period_end) <= new Date(form.period_start)) {
      newErrors.period_end = 'End date must be after start date';
    }
    if (!form.abn) newErrors.abn = 'ABN is required';
    if (!form.business_name) newErrors.business_name = 'Business name is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const url = formData ? `/api/bas-forms/${formData.id}` : '/api/bas-forms';
      const method = formData ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });

      if (response.ok) {
        const result = await response.json();
        alert(formData ? 'Form updated successfully!' : 'Form created successfully!');
        onSubmit && onSubmit(result);
        if (!formData) {
          setForm({
            form_type: 'BAS',
            period_start: '',
            period_end: '',
            abn: '',
            business_name: '',
            gst_sales: '',
            gst_purchases: '',
            gst_payable: '',
            gst_refund: '',
            net_gst: '',
            payg_withholding: '',
            payg_instalment: '',
            fbt: '',
            wine_equalisation: '',
            luxury_car: '',
            fuel_tax_credit: ''
          });
        }
      } else {
        const error = await response.json();
        alert('Error: ' + error.error);
      }
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="form-section">
      <h3>
        <i className="fas fa-file-alt me-2"></i>
        {formData ? 'Edit BAS Form' : 'New BAS Form'}
      </h3>
      
      <Form onSubmit={handleSubmit}>
        <Card className="mb-4">
          <Card.Header>Business Information</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ABN *</Form.Label>
                  <Form.Control
                    type="text"
                    name="abn"
                    value={form.abn}
                    onChange={handleChange}
                    placeholder="11 digit ABN"
                    isInvalid={!!errors.abn}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.abn}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Business Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="business_name"
                    value={form.business_name}
                    onChange={handleChange}
                    placeholder="Business name"
                    isInvalid={!!errors.business_name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.business_name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Period Start *</Form.Label>
                  <Form.Control
                    type="date"
                    name="period_start"
                    value={form.period_start}
                    onChange={handleChange}
                    isInvalid={!!errors.period_start}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.period_start}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Period End *</Form.Label>
                  <Form.Control
                    type="date"
                    name="period_end"
                    value={form.period_end}
                    onChange={handleChange}
                    isInvalid={!!errors.period_end}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.period_end}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>GST Information</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>GST Sales ($)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="gst_sales"
                    value={form.gst_sales}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>GST Purchases ($)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="gst_purchases"
                    value={form.gst_purchases}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>GST Payable ($)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="gst_payable"
                    value={form.gst_payable}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>GST Refund ($)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="gst_refund"
                    value={form.gst_refund}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Net GST ($)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="net_gst"
                    value={form.net_gst}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>Other Tax Information</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>PAYG Withholding ($)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="payg_withholding"
                    value={form.payg_withholding}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>PAYG Instalment ($)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="payg_instalment"
                    value={form.payg_instalment}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>FBT ($)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="fbt"
                    value={form.fbt}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Wine Equalisation ($)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="wine_equalisation"
                    value={form.wine_equalisation}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Luxury Car ($)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="luxury_car"
                    value={form.luxury_car}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fuel Tax Credit ($)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="fuel_tax_credit"
                    value={form.fuel_tax_credit}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-between">
          <Button 
            variant="secondary" 
            onClick={onCancel}
            disabled={isSubmitting}
          >
            <i className="fas fa-times me-1"></i>
            Cancel
          </Button>
          
          <Button 
            type="submit" 
            variant="primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <i className="fas fa-spinner fa-spin me-1"></i>
                {formData ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <i className="fas fa-save me-1"></i>
                {formData ? 'Update Form' : 'Create Form'}
              </>
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default BASForm;
